// src/components/calendar/CalendarFilters.jsx
import { useNavigate } from "react-router-dom";

/**
 * CalendarFilters Component
 *
 * Renders filter buttons for different event types with live counts.
 * Handles filter state and provides visual feedback for active filters.
 * Now includes filters for Reschedule, Pick Up, and Delivery events.
 * Supports URL-based filtering via navigation.
 */
const CalendarFilters = ({ events, activeFilter, onFilterChange }) => {
  const navigate = useNavigate();
  // Calculate event counts for each filter
  const getEventCount = (filterType) => {
    if (filterType === "all") {
      return events.length;
    }
    return events.filter((event) => event.eventType === filterType).length;
  };

  // Filter button configuration with all event types
  const filterButtons = [
    {
      id: "all",
      label: "All Events",
      baseColor: "indigo",
    },
    {
      id: "measurement",
      label: "📏 Measurement",
      baseColor: "blue",
    },
    {
      id: "installation",
      label: "🔧 Installation",
      baseColor: "green",
    },
    {
      id: "price",
      label: "💰 Pricing",
      baseColor: "purple",
    },
    {
      id: "follow-up",
      label: "📞 Follow-up",
      baseColor: "teal",
    },
    {
      id: "quote",
      label: "📋 Quote",
      baseColor: "slate",
    },
    {
      id: "reschedule",
      label: "🔄 Reschedule",
      baseColor: "orange",
    },
    {
      id: "pickup",
      label: "📋 Pick Up",
      baseColor: "cyan",
    },
    {
      id: "delivery",
      label: "🚚 Delivery",
      baseColor: "red",
    },
    {
      id: "payment",
      label: "💳 Payment",
      baseColor: "emerald",
    },
    {
      id: "repair",
      label: "🛠 Repair",
      baseColor: "amber",
    },
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 mb-4">
      <div className="flex flex-wrap gap-2">
        {filterButtons.map((button) => {
          const count = getEventCount(button.id);
          const isActive = activeFilter === button.id;

          // Define button styles based on active state and color
          const getButtonStyles = () => {
            if (isActive) {
              // Active button styles with specific colors
              switch (button.baseColor) {
                case "indigo":
                  return "bg-indigo-600 text-white shadow-md hover:bg-indigo-700";
                case "blue":
                  return "bg-blue-600 text-white shadow-md hover:bg-blue-700";
                case "green":
                  return "bg-green-600 text-white shadow-md hover:bg-green-700";
                case "purple":
                  return "bg-purple-600 text-white shadow-md hover:bg-purple-700";
                case "teal":
                  return "bg-teal-600 text-white shadow-md hover:bg-teal-700";
                case "slate":
                  return "bg-slate-600 text-white shadow-md hover:bg-slate-700";
                case "orange":
                  return "bg-orange-600 text-white shadow-md hover:bg-orange-700";
                case "cyan":
                  return "bg-cyan-600 text-white shadow-md hover:bg-cyan-700";
                case "red":
                  return "bg-red-600 text-white shadow-md hover:bg-red-700";
                default:
                  return "bg-gray-600 text-white shadow-md hover:bg-gray-700";
              }
            } else {
              // Inactive button styles with hover colors
              switch (button.baseColor) {
                case "indigo":
                  return "bg-white text-gray-700 border border-gray-300 hover:bg-indigo-50 hover:border-indigo-300";
                case "blue":
                  return "bg-white text-gray-700 border border-gray-300 hover:bg-blue-50 hover:border-blue-300";
                case "green":
                  return "bg-white text-gray-700 border border-gray-300 hover:bg-green-50 hover:border-green-300";
                case "purple":
                  return "bg-white text-gray-700 border border-gray-300 hover:bg-purple-50 hover:border-purple-300";
                case "teal":
                  return "bg-white text-gray-700 border border-gray-300 hover:bg-teal-50 hover:border-teal-300";
                case "slate":
                  return "bg-white text-gray-700 border border-gray-300 hover:bg-slate-50 hover:border-slate-300";
                case "orange":
                  return "bg-white text-gray-700 border border-gray-300 hover:bg-orange-50 hover:border-orange-300";
                case "cyan":
                  return "bg-white text-gray-700 border border-gray-300 hover:bg-cyan-50 hover:border-cyan-300";
                case "red":
                  return "bg-white text-gray-700 border border-gray-300 hover:bg-red-50 hover:border-red-300";
                default:
                  return "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50";
              }
            }
          };

          // Handle filter button click
          const handleFilterClick = () => {
            // Update URL with filter parameter
            if (button.id === "all") {
              navigate("/calendar");
            } else {
              navigate(`/calendar?eventType=${button.id}`);
            }
            
            // Also call the original callback for immediate state update
            onFilterChange(button.id);
          };

          return (
            <button
              key={button.id}
              onClick={handleFilterClick}
              className={`px-3 py-2.5 sm:px-4 sm:py-2 rounded-lg text-sm sm:text-sm font-medium transition-all duration-200 active:scale-95 ${getButtonStyles()}`}
            >
              <span className="whitespace-nowrap">{button.label} ({count})</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarFilters;
